import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { IconManager } from '@speeki/icons';

import { Card } from '@components/molecules/Card/Card';
import { ColumnsLayout } from '@components/templates/ColumnsLayout/ColumnsLayout';

import {
    countriesOptions,
    defaultSelectOption,
    relationshipOptions,
} from '@utils/dictionary';
import { useResponsive } from '@utils/hooks/useResponsive';
import { useUserOptions } from '@utils/hooks/useUserOptions';

import { LoginBanner } from '@domains/auth/molecules/LoginBanner/LoginBanner';
import { SignUpForm } from '@domains/auth/organisms/SignUpForm/SignUpForm';
import { TermsSection } from '@domains/auth/organisms/TermsSection/TermsSection';
import { yupResolver } from '@hookform/resolvers/yup';
import { passwordValid } from '@shared/validationFunctions';
import { emailValidation, requiredSelectV2 } from '@shared/validations';

import { BannerLoginText } from './SignUpPage.styled';

export const SignUpPage = () => {
    const { t } = useTranslation();
    const isMd = useResponsive('md');

    const { data } = useUserOptions();

    const formMethods = useForm<SignUpFormValues>({
        defaultValues: {
            country: defaultSelectOption(
                countriesOptions(),
                data?.user.country,
            ),
            relationship: defaultSelectOption(
                relationshipOptions(),
                data?.user.relationship,
            ),
        },
        mode: 'onChange',
        resolver: yupResolver(SignUpSchema),
    });

    return (
        <FormProvider {...formMethods}>
            <ColumnsLayout>
                <Card testId="PJyZahG">
                    <Card.Title>{t('CREATE_SPEEKI_ACCOUNT')}</Card.Title>
                    <SignUpForm />
                    {isMd && <TermsSection />}
                </Card>
                {!isMd ? (
                    <TermsSection />
                ) : (
                    <LoginBanner>
                        <IconManager icon={'SvgInfo'} size={16} />
                        <BannerLoginText />
                    </LoginBanner>
                )}
            </ColumnsLayout>
        </FormProvider>
    );
};

const SignUpSchema = yup.object({
    country: requiredSelectV2,
    email: emailValidation,
    password: yup
        .string()
        .required()
        .test('passwordValidation', '', passwordValid),
    privacyPolicy: yup
        .boolean()
        .required('THIS_CONSENT_IS_MANDATORY')
        .oneOf([true], 'THIS_CONSENT_IS_MANDATORY'),
    rePassword: yup
        .string()
        .required('FIELD_CANNOT_BE_EMPTY')
        .oneOf([yup.ref('password')], 'PASSWORD_RESET_CONFIRM_DO_NOT_MATCH'),
    relationship: requiredSelectV2,
});

export type SignUpFormValues = yup.InferType<typeof SignUpSchema>;
